import { Phone, MapPin, Navigation, CheckCircle, XCircle, QrCode, FileText, Clock, MoreVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DriverMission } from '@/hooks/useDriverMissions';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

interface DriverMissionCardProps {
  mission: DriverMission;
  onAccept?: (jobId: string) => void;
  onDecline?: (jobId: string) => void;
  onScan?: (mission: DriverMission) => void;
  onOpenDocuments?: () => void;
  onReportDelay?: (mission: DriverMission) => void;
  isAccepting?: boolean;
  isDeclining?: boolean;
}

export const DriverMissionCard = ({ 
  mission, 
  onAccept, 
  onDecline, 
  onScan,
  onOpenDocuments,
  onReportDelay,
  isAccepting,
  isDeclining 
}: DriverMissionCardProps) => {
  const { t } = useTranslation();
  const isAssigned = !mission.accepted_at && !mission.declined_at && !mission.completed_at;
  const isActive = mission.accepted_at && !mission.completed_at;
  const hasDelayReported = mission.delay_reported_at !== null;


  return (
    <Card className="bg-white rounded-xl shadow-md border border-[#DBE2EA] hover:border-[#0880AE] transition-all duration-200">
      <CardContent className="p-4 md:p-6 space-y-4 md:space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <h3 className="text-lg md:text-xl font-semibold text-[#37699E] font-['Lexend_Deca']">
              {mission.orders.order_number}
            </h3>
            <p className="text-sm md:text-base text-[#2D3748] font-['Funnel_Sans'] mt-1 break-words">
              {mission.orders.cargo_title || mission.orders.cargo_description}
            </p>
            {hasDelayReported && (
              <Badge className="bg-[#F2AC57]/10 text-[#F2AC57] text-xs mt-2">
                <Clock size={12} className="mr-1" />
                {t('missions.card.delayReported')} ({mission.estimated_delay_minutes} min)
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Badge className={cn(
              'text-xs md:text-sm px-2.5 py-1 whitespace-nowrap',
              isAssigned && 'bg-[#FFBF07]/10 text-[#2D3748]',
              isActive && 'bg-[#0880AE]/10 text-[#0880AE]',
              mission.completed_at && 'bg-[#14A38B]/10 text-[#14A38B]'
            )}>
              {isAssigned ? t('missions.status.assigned') : isActive ? t('missions.status.inProgress') : t('missions.status.completed')}
            </Badge>
            {isActive && onReportDelay && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-[#5A6B7A] hover:text-[#0880AE] hover:bg-[#EBF4F8]"
                  >
                    <MoreVertical size={20} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-white z-50">
                  <DropdownMenuItem
                    onClick={() => onReportDelay(mission)}
                    className="cursor-pointer"
                  >
                    <Clock size={16} className="mr-2 text-[#F2AC57]" />
                    {t('missions.actions.reportDelay')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>

        {/* Route */}
        <div className="space-y-3 md:space-y-4">
          <div className="flex items-start gap-3 p-4 md:p-4 bg-[#EBF4F8] rounded-lg">
            <MapPin size={24} className="text-[#0880AE] flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide font-['Funnel_Sans']">
                {t('missions.card.pickup')}
              </p>
              <p className="text-base md:text-sm text-[#2D3748] font-['Funnel_Sans'] break-words mt-1">
                {mission.orders.pickup_address}
              </p>
              <p className="text-sm md:text-xs text-[#5A6B7A] font-['Funnel_Sans'] mt-2">
                {format(new Date(mission.orders.pickup_date), 'dd MMMM yyyy - HH:mm', { locale: fr })}
              </p>
              <div className="flex flex-col sm:flex-row gap-2 md:gap-2 mt-3">
                <a 
                  href={`tel:${mission.orders.pickup_phone}`}
                  className="inline-flex items-center justify-center gap-2 px-4 py-3 md:py-2 bg-white rounded-lg text-base md:text-sm font-medium text-[#0880AE] hover:bg-[#0880AE]/10 active:bg-[#0880AE]/20 transition-colors min-h-[44px]"
                >
                  <Phone size={20} className="md:w-4 md:h-4" />
                  {mission.orders.pickup_contact}
                </a>
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(mission.orders.pickup_address)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-4 py-3 md:py-2 bg-[#37699E] hover:bg-[#2E5782] active:bg-[#254566] text-white font-semibold rounded-lg text-base md:text-sm transition-colors min-h-[44px]"
                >
                  <Navigation size={20} className="md:w-4 md:h-4" />
                  {t('missions.actions.route')}
                </a>
              </div>
            </div>
          </div>

          <div className="flex items-start gap-3 p-4 md:p-4 bg-[#EBF4F8] rounded-lg">
            <MapPin size={24} className="text-[#14A38B] flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide font-['Funnel_Sans']">
                {t('missions.card.delivery')}
              </p>
              <p className="text-base md:text-sm text-[#2D3748] font-['Funnel_Sans'] break-words mt-1">
                {mission.orders.delivery_address}
              </p>
              <p className="text-sm md:text-xs text-[#5A6B7A] font-['Funnel_Sans'] mt-2">
                {format(new Date(mission.orders.delivery_date), 'dd MMMM yyyy - HH:mm', { locale: fr })}
              </p>
              <div className="flex flex-col sm:flex-row gap-2 md:gap-2 mt-3">
                <a 
                  href={`tel:${mission.orders.delivery_phone}`}
                  className="inline-flex items-center justify-center gap-2 px-4 py-3 md:py-2 bg-white rounded-lg text-base md:text-sm font-medium text-[#0880AE] hover:bg-[#0880AE]/10 active:bg-[#0880AE]/20 transition-colors min-h-[44px]"
                >
                  <Phone size={20} className="md:w-4 md:h-4" />
                  {mission.orders.delivery_contact}
                </a>
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(mission.orders.delivery_address)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-4 py-3 md:py-2 bg-[#37699E] hover:bg-[#2E5782] active:bg-[#254566] text-white font-semibold rounded-lg text-base md:text-sm transition-colors min-h-[44px]"
                >
                  <Navigation size={20} className="md:w-4 md:h-4" />
                  {t('missions.actions.route')}
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        {isAssigned && onAccept && onDecline && (
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <Button
              onClick={() => onDecline(mission.id)}
              disabled={isDeclining || isAccepting}
              className="flex-1 bg-[#FF7171] hover:bg-[#E65959] active:bg-[#E65959] text-white font-semibold py-4 md:py-3 text-base min-h-[48px] md:min-h-[44px]"
            >
              <XCircle size={20} className="mr-2" />
              {t('missions.actions.refuse')}
            </Button>
            <Button
              onClick={() => onAccept(mission.id)}
              disabled={isAccepting || isDeclining}
              className="flex-1 bg-[#14A38B] hover:bg-[#10836F] active:bg-[#10836F] text-white font-semibold py-4 md:py-3 text-base min-h-[48px] md:min-h-[44px]"
            >
              <CheckCircle size={20} className="mr-2" />
              {t('missions.actions.accept')}
            </Button>
          </div>
        )}

        {isActive && onScan && (
          <div className="flex flex-col gap-3 pt-2">
            <Button
              onClick={() => onScan(mission)}
              className="w-full bg-[#FFBF07] hover:bg-[#E5AB06] active:bg-[#CC9805] text-[#2D3748] font-semibold py-4 md:py-3 text-base min-h-[48px] md:min-h-[44px]"
            >
              <QrCode size={20} className="mr-2" />
              {t('missions.actions.scanQR')}
            </Button>
            {onOpenDocuments && (
              <Button
                onClick={onOpenDocuments}
                variant="outline"
                className="w-full border-2 border-[#37699E] text-[#37699E] hover:bg-[#EBF4F8] active:bg-[#DBE2EA] font-semibold py-4 md:py-3 text-base min-h-[48px] md:min-h-[44px]"
              >
                <FileText size={20} className="mr-2" />
                {t('missions.actions.documents')}
              </Button>
            )}
          </div>
        )}

        {mission.completed_at && onOpenDocuments && (
          <Button
            onClick={onOpenDocuments}
            className="w-full bg-[#37699E] hover:bg-[#2E5782] active:bg-[#254566] text-white font-semibold py-4 md:py-3 text-base min-h-[48px] md:min-h-[44px]"
          >
            <FileText size={20} className="mr-2" />
            {t('missions.actions.viewDocuments')}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};
